import BaseComponent from "./base_component_controller"

import URL from "hb/lib/url"
import { useResize } from "hb/insights/use_resize"
import { useChartConfig } from "hb/insights/use_chart_config"
import { valueFormatter } from "hb/insights/chart"
import {
  nestedGroupByToMap, nestedMapGet, extractCascade
} from "hb/lib/collection_helpers"
import ColorInterpolator from "hb/insights/color_interpolator"

export default class extends BaseComponent {
  static viewId = "heatmap"

  static get values() {
    return {
      ...BaseComponent.values,

      querySessionId: String,
      schema: Array,
      results: Array,
      chartConfig: Object
    }
  }

  static configs = [
    "xField",
    "yField",
    "yFieldUnit",
    "zField",

    {name: "steps", default: 5},
    {name: "groups", default: "{}", type: "json"},
  ]

  connect() {
    super.connect()
    useChartConfig(this);
  }

  outletsConnected() {
    super.outletsConnected()
    useResize(this, {element: this.outletComponent("chart").chartTarget})
    this.initQuery();
  }

  splitRangeIntoSteps(min, max, steps) {
    const range = max - min;
    const stepSize = Math.round(range / steps);
    const pieces = [];

    for (let i = 0; i < steps; i++) {
      const pieceMin = min + i * stepSize;
      const pieceMax = i === steps - 1 ? max : pieceMin + stepSize;
      pieces.push({
        min: pieceMin,
        max: pieceMax
      });
    }

    return pieces;
  }

  processConfig() {
    const {results} = this.query;
    let {zField, steps, groups} = this.config;

    const z = results.map(d => d.field(zField))
    const zmin = Math.floor(Math.min(...z))
    const zmax = Math.ceil(Math.max(...z))

    let colorIdxes = []
    // const colorIdxes = incrementedIds(steps, i => roundToDecimalPlace(i / (steps-1), 3))
    for (let i = 0; i < steps; i++) {
      if (i === 0) {
        colorIdxes[i] = groups[i]?.color || this.colorMapValue["yellow"]
      } else if (i === steps - 1) {
        colorIdxes[i] = groups[i]?.color || this.colorMapValue["red"]
      } else {
        colorIdxes[i] = groups[i]?.color || null
      }
    }

    colorIdxes = new ColorInterpolator().interpolateColors(colorIdxes)

    const splits = this.splitRangeIntoSteps(zmin, zmax, steps)
    this.derivedConfig.pieces = splits.map(({min, max}, idx) => ({
      min, max,
      color: colorIdxes[idx],
    }))

    this.derivedConfig.groupConfig = splits.map(({min, max}, idx) => ({
      idx,
      id: idx,
      color: colorIdxes[idx],
      colorSet: !!groups[idx]?.color,
      name: `${min} - ${max}`
    }))
  }

  legendHover({entered, seriesIndex}) {
    seriesIndex = parseInt(seriesIndex, 10)
    if (!entered) {
      this.derivedConfig.pieces = this.derivedConfig.pieces.map(({min, max, color}) => ({
        min, max, color
      }))
    } else {
      this.derivedConfig.pieces = this.derivedConfig.pieces.map(({min, max, color}, idx) => ({
        min, max, color,
        opacity: idx === seriesIndex ? 1 : 0.8,
      }))
    }

    this.render()
  }

  legendSelected() {
    // Doing nothing for now
  }

  renderConfig() {
    const {meta} = this.query;

    const allFields = meta.fields.map(field =>
      this.comboboxItemTemplate({
        value: field,
        id: field,
        display: field
      })
    ).join("\n");

    ["xField", "yField", "zField"].forEach(name => {
      this.find(`[data-input-name="${name}"] ul`).innerHTML = allFields;
    })

    this.find(`input[name="xField"]`).value = this.config.xField || "";
    this.find(`input[name="yField"]`).value = this.config.yField || "";
    this.find(`input[name="zField"]`).value = this.config.zField || "";

    this.renderFieldGroups(this.derivedConfig.groupConfig)
    this.outletComponent("legend").render(this.derivedConfig.groupConfig)
  }

  dashboardRender() {
    this.outletComponent("legend").render(this.derivedConfig.groupConfig)
  }

  // Replace the name with the index of the color
  updateColor({detail: {id, color}}) {
    super.updateColor({detail: {name: id, color}})
    this.derivedConfig.groupConfig.forEach(({color, idx}) => {
      this.find(`[data-field-group-id="${idx}"] input`).value = color;
    })
  }

  inputUpdate(evt) {
    // Steps are special since we need to update the groups as well
    if (evt.detail.name === "steps") {
      const prevSteps = this.config.steps;
      let nextSteps;
      if (evt.detail.value === "") {
        nextSteps = 5
      } else {
        nextSteps = parseInt(evt.detail.value, 10)
      }
      const newKeys = Object.keys(this.config.groups).map(k => {
        return Math.floor((parseInt(k, 10) / (prevSteps - 1)) * (nextSteps - 1))
      })

      this.config.groups = Object.fromEntries(
        Object.entries(this.config.groups).map(([, v], idx) => [newKeys[idx], v])
      )
      this.config.steps = nextSteps;

      URL.replace(URL.mergeParams({
        steps: nextSteps,
        groups: JSON.stringify(this.config.groups)
      }))

      this.processConfig()
      this.validate()
      this.renderConfig()
      this._render()
    } else {
      super.inputUpdate(evt)
    }
  }

  queryContextDefaults() {
    const {meta} = this.query;
    let config = {};
    const fieldTypes = Object.entries(meta.fieldTypes);

    const xRes = extractCascade(fieldTypes,
      ([, t]) => t === "datetime",
      ([, t]) => (t === "integer" || t === "float")
    )

    const yRes = extractCascade(xRes[1],
      ([, t]) => t === "string",
      ([, t]) => t === "integer" || t === "float"
    )

    const zRes = extractCascade(yRes[1],
      ([, t]) => t === "integer" || t === "float",
    )

    if(xRes[0] !== undefined) config.xField = xRes[0][0]
    if(yRes[0] !== undefined) config.yField = yRes[0][0]
    if(zRes[0] !== undefined) config.zField = zRes[0][0]

    return config;
  }

  validate() {
    let {xField, yField, zField} = this.config;

    if(!xField || !yField || !zField) {
      this.errorMessage = "Select fields in chart config or update data to fit chart"
    } else if (this.hasUnknownFields(xField, yField, zField)) {
      this.errorMessage = "Unknown field selected"
    }
  }

  render() {
    let {results} = this.query;
    let {yField, yFieldUnit, zField, xField} = this.config;

    const X = results.map(d => d.field(xField));
    const xDomain = Array.from(new Set(X));
    const Y = results.map(d => d.field(yField));
    const yDomain = Array.from(new Set(Y));

    const nested = nestedGroupByToMap(results,
      r => [r.field(xField), r.field(yField)],
      r => r.field(zField)
    )

    let coords = []
    xDomain.forEach((x) => {
      yDomain.forEach((y) => {
        coords.push([x, y, nestedMapGet(nested, [x, y])])
      })
    })

    let yAxis = {
      type: "category",
      splitArea: {
        show: true
      }
    }

    let tooltip = {
      ...this.defaultChartConfig.tooltip,
      trigger: "item"
    }

    const yFieldFormatter = valueFormatter(yFieldUnit)
    if(yFieldFormatter) {
      yAxis.axisLabel = {formatter: yFieldFormatter}
    }

    const options = {
      ...this.defaultChartConfig,
      series: {
        type: "heatmap",
        data: coords,
      },
      xAxis: {
        type: "category",
        splitArea: {
          show: true
        },
      },
      visualMap: {
        show: false,
        type: "piecewise",
        showLabel: false,
        itemGap: 2,
        pieces: this.derivedConfig.pieces,
        orient: "horizontal",
      },
      tooltip,
      yAxis
    }

    this.outletComponent("chart").setChartOptions(options)
  }
}
